import { computed, inject, Injectable, signal, Signal, WritableSignal } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { CustomerModel } from '@libs/account/models';
import {
  ConditionModel,
  FileInputValueModel,
  GroupValueType,
  InputItemModel,
  InputValueType,
  isInputItem,
  isInputListItem,
  MultiStepFormItemModel,
  SelectInputModel,
  SelectOptionModel,
  StepItemModel,
} from '@libs/form/models';
import type {
  OrderHistoryRecordModel,
  SrmCategoryTreeModel,
  SrmTicketCreateModel,
  SrmTicketSuggestionModel,
  SrmTicketSuggestionRequestModel,
} from '@libs/services/models';
import { AuthService } from '@libs/shared/auth';
import { FlashMessageService } from '@libs/shared/common';
import { CapabilityService } from '@libs/shared/services';
import { SrmTrackingLabels, SrmTrackingLocation, SrmTrackingStep } from '@libs/shared/models';
import { catchError, firstValueFrom, map, Observable, of, throwError } from 'rxjs';
import { OrderHistoryHelperService } from '../../order-history/order-history-helper.service';
import { CMS_PAGE_ROUTE_CONTEXT } from '../../cms-page-route-context';
import { SrmService, SrmTrackingService } from '../services';
import {
  HintRequired,
  SrmCreateForm,
  SrmCreateStepRequestDetails,
  SrmCreateStepRequestReason,
  SrmCreateStepSuccess,
  SrmCreateStepSummary,
  SrmCustomerNumberSelectItem,
  SrmRequestReasonSelectTreeItem,
  SrmTicketSuggestionItem,
} from './srm-create-form.config';

/**
 * Tracking events emitted when the user leaves each step.
 * `forward` fires after a successful submit; `back` fires on the `prev` button (where present).
 */
type TrackingMap = Record<string, { forward: SrmTrackingStep | null; back?: SrmTrackingStep }>;
const trackingMap: TrackingMap = {
  requestReason: { forward: '2-anliegen' },
  'srm-request-details': { forward: '3-details-weiter', back: '3-details-zurück' },
  summary: { forward: '4-zusammenfassung-weiter', back: '4-zusammenfassung-zurück' },
  success: { forward: null },
};

@Injectable()
export class SrmCreateFormService {
  private readonly authService: AuthService = inject(AuthService);
  private readonly capabilityService: CapabilityService = inject(CapabilityService);
  private readonly flashMessageService: FlashMessageService = inject(FlashMessageService);
  private readonly orderHistoryHelperService: OrderHistoryHelperService = inject(OrderHistoryHelperService);
  private readonly srmService: SrmService = inject(SrmService);
  private readonly trackingService: SrmTrackingService = inject(SrmTrackingService);
  private readonly hasCmsPageRouteContext: boolean = Boolean(inject(CMS_PAGE_ROUTE_CONTEXT, { optional: true }));

  private readonly location: WritableSignal<SrmTrackingLocation | undefined> = signal(undefined);

  private readonly order: WritableSignal<OrderHistoryRecordModel | undefined> = signal(undefined);
  private readonly categoryTree: WritableSignal<SrmCategoryTreeModel[]> = signal([]);

  private readonly formControl: WritableSignal<FormGroup | undefined> = signal(undefined);

  private readonly selectedCategoryTicketSuggestion: WritableSignal<boolean> = signal(false);
  private readonly isSummaryTicketSuggestionShown: Signal<boolean> = computed(() =>
    // hide the suggestion in CMS because the form may be embedded in a page without the SRM overview route
    !this.hasCmsPageRouteContext &&
    // for now only enabled in category 3, "documents-missing-form"
    this.capabilityService.isAvailable('services.srmTicketSuggestion')() && this.selectedCategoryTicketSuggestion(),
  );

  private readonly stepReason: WritableSignal<StepItemModel> = signal({ ...SrmCreateStepRequestReason });
  private readonly stepDetails: WritableSignal<StepItemModel> = signal({ ...SrmCreateStepRequestDetails });

  private readonly stepSummary: Signal<StepItemModel> = computed(() => {
    const items: StepItemModel['items'] = this.isSummaryTicketSuggestionShown()
      ? [...SrmCreateStepSummary.items, SrmTicketSuggestionItem]
      : SrmCreateStepSummary.items;

    const stepSummary: StepItemModel = {
      ...SrmCreateStepSummary,
      items,
    };

    return stepSummary;
  });

  private readonly stepSuccess: Signal<StepItemModel> = computed(() => ({
    ...SrmCreateStepSuccess,
    // hide the action in CMS because the form may be embedded in a page without the SRM overview route
    submit: this.hasCmsPageRouteContext ? undefined : SrmCreateStepSuccess.submit,
  }));

  private readonly currentCustomer: Signal<CustomerModel | undefined> = this.authService.currentCustomer;
  private readonly customers: Signal<CustomerModel[] | undefined> = this.authService.allCustomers;

  // ==================== Form Methods ====================

  public async initForm(order?: OrderHistoryRecordModel, location?: SrmTrackingLocation, formControl?: FormGroup): Promise<MultiStepFormItemModel> {
    this.location.set(location);
    this.order.set(order);
    this.formControl.set(formControl);
    this.categoryTree.set(await this.loadCategoryTree(location));
    this.stepReason.set(this.buildReasonStep());

    this.track('1-start');

    return {
      ...SrmCreateForm,
      items: [
        this.stepReason(),
        this.stepDetails(),
        this.stepSummary(),
        this.stepSuccess(),
      ],
    };
  }

  public async submitStep(currentStep: StepItemModel, fullFormValue: GroupValueType): Promise<StepItemModel | undefined> {
    const submission: Observable<StepItemModel | undefined> | undefined = this.createStepSubmission(currentStep, fullFormValue);
    const nextStep: StepItemModel | undefined = submission ? await firstValueFrom(submission) : undefined;

    // Fires only after the transition above resolves, so a failed BFF call does not emit a tracking event.
    const forward: SrmTrackingStep | null | undefined = trackingMap[currentStep.name]?.forward;

    if (forward) {
      this.track(forward);
    }

    return nextStep;
  }

  /**
   * Tracks the `zurück` event for the step being left, if it has one.
   */
  public trackBack(stepName: string): void {
    const back: SrmTrackingStep | undefined = trackingMap[stepName]?.back;

    if (back) {
      this.track(back);
    }
  }

  /**
   * Tracks `5-fertig` when the user clicks "done" on the success step.
   */
  public trackDone(): void {
    this.track('5-fertig');
  }

  public requestTicketSuggestion(): Promise<SrmTicketSuggestionModel> {
    const fullFormValue: GroupValueType = this.getFullFormValue();
    const data: SrmTicketCreateModel = this.formatTicketData(fullFormValue);
    const payload: SrmTicketSuggestionRequestModel = {
      customerNumber: data.customerNumber,
      selectedCategories: data.categories,
      form: data.form,
    };

    return firstValueFrom(this.srmService.getTicketSuggestion(payload));
  }

  private createStepSubmission(
    currentStep: StepItemModel,
    fullFormValue: GroupValueType,
  ): Observable<StepItemModel | undefined> | undefined {
    switch (currentStep.name) {
      case this.stepReason().name:
        return this.submitReasonStep(currentStep, fullFormValue);
      case this.stepDetails().name:
        // swap in a summary refreshed for the picked category (e.g. with/without ticket suggestion)
        return of(this.stepSummary());
      case this.stepSummary().name:
        return this.submitFormData(fullFormValue);
      default:
        return undefined;
    }
  }

  // ==================== Tracking ====================

  private track(srmStep: SrmTrackingStep): void {
    const location: SrmTrackingLocation | undefined = this.location();

    if (!location) {
      return;
    }

    const fullFormValue: GroupValueType = this.getFullFormValue();
    const reasonValue: Record<string, string> | undefined = fullFormValue[this.stepReason().name] as Record<string, string> | undefined;

    this.trackingService.trackSrmRequest({
      srmStep,
      location: `/${location}`,
      ...this.getTrackingLabels(reasonValue),
    });
  }

  /**
   * Resolves picked node titles at depth 0/1/2 to category/issue/topic tracking values.
   * Unpicked depths are null.
   */
  private getTrackingLabels(reasonStepValue: Record<string, string> | undefined): SrmTrackingLabels {
    const empty: SrmTrackingLabels = { srmCategory: null, srmIssue: null, srmTopic: null };

    if (!reasonStepValue) {
      return empty;
    }

    const ids: number[] = this.getSelectedCategories(reasonStepValue);
    const titles: (string | null)[] = [];

    let nodes: SrmCategoryTreeModel[] | undefined = this.categoryTree();

    for (const id of ids) {
      const match: SrmCategoryTreeModel | undefined = nodes?.find(node => node.id === id);

      titles.push(match ? match.title : null);
      nodes = match?.categories;
    }

    return {
      srmCategory: titles[0] ?? null,
      srmIssue: titles[1] ?? null,
      srmTopic: titles[2] ?? null,
    };
  }

  // ==================== Form Submission ====================

  private submitReasonStep(stepReason: StepItemModel, fullFormValue: GroupValueType): Observable<StepItemModel> {
    const stepValue: Record<string, string> = fullFormValue[stepReason.name] as Record<string, string>;
    const selectedCategories: number[] = this.getSelectedCategories(stepValue);
    const selectedCategory: SrmCategoryTreeModel | undefined = this.getSelectedLeafCategory(selectedCategories);

    this.selectedCategoryTicketSuggestion.set(Boolean(selectedCategory?.ticketSuggestion));

    const formIdentifier: string | undefined = selectedCategory?.formIdentifier;

    if (!formIdentifier) {
      return throwError(() => ({ required: true }));
    }

    return this.srmService.getDetailsStep(formIdentifier).pipe(
      map(step => {
        this.stepDetails.set(this.enrichDetailsStep(step));

        return this.stepDetails();
      }),
      catchError(() => {
        this.flashMessageService.showMessage('errorSrmLoadForm');

        return throwError(() => ({ srmDetailFormId: true }));
      }),
    );
  }

  private submitFormData(formValue: GroupValueType): Observable<undefined> {
    const srmTicket: SrmTicketCreateModel = this.formatTicketData(formValue);

    return this.srmService.createTicket(srmTicket).pipe(
      map(() => void 0),
      catchError(() => {
        this.flashMessageService.showMessage('errorSrmCreateTicket');

        return throwError(() => ({ unknown: true }));
      }),
    );
  }

  // ==================== Form Builder ====================

  private async loadCategoryTree(location?: string): Promise<SrmCategoryTreeModel[]> {
    return firstValueFrom(this.srmService.getCategoryTree(location))
      .catch(() => {
        this.flashMessageService.showMessage('errorSrmLoadForm');

        return [];
      });
  }

  private buildReasonStep(): StepItemModel {
    // Customer select input
    const customers: CustomerModel[] = this.customers() ?? [];
    const customerNumber: string | undefined = this.order()?.customerNumber ?? this.currentCustomer()?.id;
    const readOnly: boolean = Boolean(this.order()?.customerNumber);

    const customersToOptions = (customer: CustomerModel): SelectOptionModel => ({
      label: `${customer.id} - ${customer.name}`,
      value: customer.id,
    });

    const customerSelectItem: InputItemModel = {
      ...SrmCustomerNumberSelectItem,
      options: customers.map(customersToOptions),
      value: customerNumber,
      attributes: {
        ...SrmCustomerNumberSelectItem.attributes,
        readOnly,
      },
    };

    // Category tree input
    const categoryTree: SrmCategoryTreeModel[] = this.categoryTree();

    const selectInputs: SelectInputModel[] = [];
    const rootNode: SrmCategoryTreeModel = {
      id: 0,
      title: '',
      required: true,
      ticketSuggestion: false,
      categories: categoryTree,
    };

    this.createInputsFromSrmTree(rootNode, selectInputs);

    return {
      ...SrmCreateStepRequestReason,
      items: [
        customerSelectItem,
        ...selectInputs,
      ],
    };
  }

  private createInputsFromSrmTree(node: SrmCategoryTreeModel, inputs: SelectInputModel[], level: number = 0, parent?: SelectInputModel): void {
    const children: SrmCategoryTreeModel[] | undefined = node.categories;

    if (!children?.length) {
      return;
    }

    const selectInput: SelectInputModel = {
      type: 'select',
      name: `category-level-${level}-${node.id}`,
      label: children?.at(0)?.categoriesLabel ?? SrmRequestReasonSelectTreeItem.label,
      description: node.description,
      emptyOption: SrmRequestReasonSelectTreeItem.emptyOption,
      hint: SrmRequestReasonSelectTreeItem.hint,
      options: children.map(child => ({
        label: child.title,
        value: `category-level-${level + 1}-${child.id}`,
      })),
      attributes: {
        required: SrmRequestReasonSelectTreeItem.attributes?.required,
      },
    };

    if (parent) {
      const condition: ConditionModel = {
        dependency: parent.name,
        operator: 'equal',
        value: selectInput.name,
      };

      selectInput.conditions = {
        enabled: condition,
        visible: condition,
      };
    }

    inputs.push(selectInput);

    children.forEach(category => void this.createInputsFromSrmTree(category, inputs, level + 1, selectInput));
  }

  private enrichDetailsStep(stepDetails: StepItemModel): StepItemModel {
    const order: OrderHistoryRecordModel | undefined = this.order();

    stepDetails.items.forEach(item => {
      // Order articles autocomplete
      if (item.type === 'list') {
        item.items.forEach(child => {
          if (child.attributes?.required) {
            child.hint = HintRequired;
          }
        });

        if (item.name === 'articles' && order?.items.length) {
          item.addItemLabel = 'Artikel hinzufügen';
          item.items[0] = {
            ...item.items[0],
            type: 'autocomplete',
            allowArbitraryValue: true,
            options: order?.items.map(orderItem => ({
              label: `${orderItem.sku} - ${orderItem.productData?.details.name ?? orderItem.name}`,
              value: orderItem.sku,
            })),
          };
        }
      }

      if (!isInputItem(item)) {
        return;
      }

      if (item.attributes?.required) {
        item.hint = HintRequired;
      }

      // File upload callback
      if (item.type === 'file') {
        item.uploadCallback = this.srmService.uploadFile.bind(this.srmService);
      }

      // Prefill the "Rechnungsnummer" field (SRM names it 'order'). SAP only assigns the invoice number
      // once the order is invoiced, so uninvoiced orders fall back to the SAP document number
      if (item.name === 'order' && order) {
        const invoiceNumber: string = this.orderHistoryHelperService.invoiceNumber(order);

        if (invoiceNumber) {
          item.value = invoiceNumber;
          item.attributes = {
            ...item.attributes,
            readOnly: true,
          };
        }
      }
    });

    return {
      ...SrmCreateStepRequestDetails,
      ...stepDetails,
      items: [
        ...stepDetails.items,
      ],
    };
  }

  // ==================== Form Helper ====================

  private getSelectedCategories(reasonStepValue: Record<string, string>): number[] {
    return Object.values(reasonStepValue)
      .filter(value => value.startsWith('category-level'))
      .sort()
      .map(value => Number(value.split('-').pop()));
  }

  private getSelectedLeafCategory(selectedCategories: number[]): SrmCategoryTreeModel | undefined {
    let nodes: SrmCategoryTreeModel[] = this.categoryTree();
    let leaf: SrmCategoryTreeModel | undefined;

    for (const category of selectedCategories) {
      const node: SrmCategoryTreeModel | undefined = nodes?.find(node => node.id === category);

      if (node?.categories?.length) {
        nodes = node.categories;

        continue;
      }

      leaf = node;
    }

    return leaf;
  }

  private formatTicketData(formValue: GroupValueType): SrmTicketCreateModel {
    const reasonValue: Record<string, string> = formValue[this.stepReason().name] as Record<string, string>;
    const detailsValue: GroupValueType = formValue[this.stepDetails().name] as GroupValueType;

    const customerNumberValue: string = reasonValue['customer_number'];
    const categoryValue: number[] = this.getSelectedCategories(reasonValue);

    const detailsValueFormatted: Record<string, unknown> = {};

    this.stepDetails().items.forEach(item => {
      if (!isInputItem(item) && !isInputListItem(item)) {
        return;
      }

      const value: InputValueType = detailsValue[item.name];

      if (value === null || value === undefined) {
        return;
      }

      if (item.type === 'list') {
        detailsValueFormatted[item.name] = Object.values(value);

        return;
      }

      if (item.type === 'file') {
        const filesIds: string[] = (value as FileInputValueModel[])?.map(value => value.fileIdentifier as string);

        detailsValueFormatted[item.name] = item.attributes?.multiple ? filesIds : filesIds[0];

        return;
      }

      detailsValueFormatted[item.name] = value;
    });

    return {
      customerNumber: customerNumberValue,
      categories: categoryValue,
      form: detailsValueFormatted,
    };
  }

  private getFullFormValue(): GroupValueType {
    return this.formControl()?.value as GroupValueType;
  }
}
